/**
 * The one home for "this conversation changed, refetch it".
 *
 * Every write in the slice funnels through one of the two functions below, so which
 * queries a conversation write makes stale is decided here and nowhere else:
 *
 * - `useConversationRefresh(cid)` — after a write to a message (post, edit, delete, pin):
 *   the same `message.changed` the channel routes announce, plus `comment.changed` on an
 *   order's internal notes, whose messages are the order's team comments. Through the
 *   shared refresh map, so the writer and every other tab refresh alike.
 * - `useChannelInvalidate(cid)` — the conversation's OWN queries only (its row, thread,
 *   members, unread cursor, prompt actions, read receipts), for a caller that knows the
 *   cid but not the `Channel.id` an event names.
 *
 * Keys come only from the generated `queryKeys`, so a key-shape change in the client
 * cannot leave one of them silently matching nothing.
 */

import { queryKeys, useInvalidateQueries } from "@birdwatch/client";
import type { QueryKey } from "@tanstack/react-query";
import { useCallback } from "react";

import { refreshKeysFor } from "@/features/shared/realtime-refresh";
import {
  CHANNEL_TYPE_ORDER_COMMENT,
  parseChannelCid,
} from "../_libs/channel-cid";

/** Every query that belongs to one conversation. A params prefix: any `take`, any query. */
export function channelQueryKeys(cid: string) {
  return [
    queryKeys.getChannelsByCid({ cid }),
    queryKeys.getChannelsMessagesByCid({ cid }),
    queryKeys.getChannelsMembersByCid({ cid }),
    queryKeys.getChannelsUnreadByCid({ cid }),
    queryKeys.getChannelsPromptBlocksByCid({ cid }),
    queryKeys.getChannelsMessagesReadsByCidByMessageId({ cid }),
  ];
}

/**
 * Returns a stable `invalidate()` that refetches every query belonging to `cid` —
 * thread, pins, unread state, members, prompt actions and the channel row itself.
 *
 * Scoped to the cid rather than to a hand-picked subset: the effects are all cheap
 * reads, and a caller who has to remember which of six keys a given write touches is
 * exactly the caller who forgets one.
 */
export function useChannelInvalidate(cid: string) {
  const invalidate = useInvalidateQueries();

  return useCallback(() => {
    if (!cid) return;
    return invalidate(...channelQueryKeys(cid));
  }, [invalidate, cid]);
}

/** One write to a conversation, as the route that took it announces it. */
export type ConversationWrite = {
  /** The `Channel.id` (never a cid). */
  channelId: string;
  /** The message written; `null` for a write that names none. */
  messageId: string | null;
};

/**
 * Returns a stable `refresh(write, alsoStale?)` for a write to one message of `cid` —
 * what the route's own announcement refreshes in every other tab, done here for the
 * writer — plus whatever else the caller knows the write made stale.
 *
 * Everything is refreshed as ONE set (`invalidate` de-duplicates it): the events share
 * keys (the thread's messages), and invalidating one query twice in a tick cancels the
 * first refetch, so a caller awaiting it would resume before the data landed.
 */
export function useConversationRefresh(cid: string) {
  const invalidate = useInvalidateQueries();

  return useCallback(
    (
      { channelId, messageId }: ConversationWrite,
      alsoStale: readonly QueryKey[] = [],
    ) => {
      const parsed = parseChannelCid(cid);
      const keys = [
        ...refreshKeysFor("message.changed", { channelId, messageId }),
        ...(parsed?.channelTypeId === CHANNEL_TYPE_ORDER_COMMENT && messageId
          ? refreshKeysFor("comment.changed", {
              orderId: parsed.entityId,
              commentId: messageId,
            })
          : []),
        ...alsoStale,
      ];
      return invalidate(...keys);
    },
    [invalidate, cid],
  );
}
